import { useState } from 'react';

import { getAdminAuditLogsApi } from '@/lib/api/adminAuditLogApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import { AdminAuditAction } from '@shared/enums/domain.enum';
import { useQuery } from '@tanstack/react-query';
import { Activity, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

// ─── Constants ─────────────────────────────────────────────────

const LIMIT = 20;

const ACTION_LABELS: Record<AdminAuditAction, string> = {
  [AdminAuditAction.IMPERSONATE_START]: 'Impersonation démarrée',
  [AdminAuditAction.IMPERSONATE_STOP]: 'Impersonation arrêtée',
  [AdminAuditAction.SAAS_PARAMS_UPDATE]: 'Paramètres plateforme',
  [AdminAuditAction.CLAIM_APPROVE]: 'Demande club approuvée',
  [AdminAuditAction.CLAIM_REJECT]: 'Demande club rejetée',
  [AdminAuditAction.REFUND_APPROVE]: 'Demande refund approuvée',
  [AdminAuditAction.REFUND_REJECT]: 'Demande refund rejetée',
  [AdminAuditAction.USER_ROLE_CHANGE]: 'Changement de rôle',
  [AdminAuditAction.ORG_ADMIN_REQUEST_ACCEPT]: 'Demande org acceptée',
  [AdminAuditAction.ORG_ADMIN_REQUEST_REJECT]: 'Demande org rejetée',
  [AdminAuditAction.ORG_ADMIN_PING]: 'Ping admins org',
  [AdminAuditAction.CMS_OFFER_CHANGE]: 'Activation du site vitrine',
  [AdminAuditAction.CMS_DOMAIN_CHANGE]: 'Domaine du site vitrine',
};

const ACTION_BADGE: Record<AdminAuditAction, string> = {
  [AdminAuditAction.IMPERSONATE_START]: 'bg-red-100 text-red-700',
  [AdminAuditAction.IMPERSONATE_STOP]: 'bg-red-100 text-red-700',
  [AdminAuditAction.SAAS_PARAMS_UPDATE]: 'bg-amber-100 text-amber-700',
  [AdminAuditAction.CLAIM_APPROVE]: 'bg-emerald-100 text-emerald-700',
  [AdminAuditAction.CLAIM_REJECT]: 'bg-slate-100 text-slate-600',
  [AdminAuditAction.REFUND_APPROVE]: 'bg-emerald-100 text-emerald-700',
  [AdminAuditAction.REFUND_REJECT]: 'bg-rose-100 text-rose-700',
  [AdminAuditAction.USER_ROLE_CHANGE]: 'bg-purple-100 text-purple-700',
  [AdminAuditAction.ORG_ADMIN_REQUEST_ACCEPT]: 'bg-emerald-100 text-emerald-700',
  [AdminAuditAction.ORG_ADMIN_REQUEST_REJECT]: 'bg-slate-100 text-slate-600',
  [AdminAuditAction.ORG_ADMIN_PING]: 'bg-blue-100 text-blue-700',
  [AdminAuditAction.CMS_OFFER_CHANGE]: 'bg-blue-100 text-blue-700',
  [AdminAuditAction.CMS_DOMAIN_CHANGE]: 'bg-sky-100 text-sky-700',
};

// ─── Panel ─────────────────────────────────────────────────────

export default function ActivityPanel() {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState<AdminAuditAction | ''>('');

  const params = {
    page,
    limit: LIMIT,
    ...(actionFilter !== '' ? { action: actionFilter } : {}),
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: queryKeys.auditLogs(params),
    queryFn: () => getAdminAuditLogsApi({ page, limit: LIMIT, action: actionFilter || undefined }),
    enabled: isAuthenticated,
  });

  // Signal error via toast (run once per error instance)
  if (isError) {
    toast.error(error instanceof Error ? error.message : 'Erreur lors du chargement des logs');
  }

  const loading = isLoading || !isAuthenticated;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / LIMIT)) : 1;

  function handleActionChange(e: React.ChangeEvent<HTMLSelectElement>) {
    setActionFilter(e.target.value as AdminAuditAction | '');
    setPage(1);
  }

  // ── Loading ───────────────────────────────────────────────────
  if (loading) {
    return (
      <div className='flex items-center justify-center h-48'>
        <div className='w-8 h-8 border-4 border-red-200 border-t-red-600 rounded-full animate-spin' />
      </div>
    );
  }

  return (
    <div className='space-y-6 max-w-4xl'>
      {/* ── Header card ──────────────────────────────────────── */}
      <div className='bg-white rounded-xl border border-slate-200 p-6'>
        <div className='flex items-center justify-between gap-4 mb-6'>
          <div className='flex items-center gap-3'>
            <div className='w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center'>
              <Activity className='w-5 h-5 text-slate-600' />
            </div>
            <div>
              <h2 className='text-lg font-bold text-slate-900'>Journal d&apos;activité</h2>
              <p className='text-sm text-slate-500'>Actions sensibles des super-admins</p>
            </div>
          </div>

          {/* Filtre action */}
          <select
            value={actionFilter}
            onChange={handleActionChange}
            className='px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-700 focus:border-red-500 focus:outline-none bg-white'
          >
            <option value=''>Toutes les actions</option>
            {Object.values(AdminAuditAction).map(action => (
              <option key={action} value={action}>
                {ACTION_LABELS[action]}
              </option>
            ))}
          </select>
        </div>

        {/* ── Table ────────────────────────────────────────── */}
        {!data || data.data.length === 0 ? (
          <div className='flex flex-col items-center justify-center py-16 text-center'>
            <div className='w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-4'>
              <Activity className='w-6 h-6 text-slate-400' />
            </div>
            <p className='text-sm font-semibold text-slate-700'>Aucune action enregistrée</p>
            <p className='text-xs text-slate-400 mt-1'>Les actions sensibles apparaîtront ici</p>
          </div>
        ) : (
          <div className='overflow-x-auto'>
            <table className='w-full text-sm'>
              <thead>
                <tr className='border-b border-slate-100'>
                  <th className='text-left text-xs font-semibold uppercase tracking-wider text-slate-400 pb-3 pr-4 whitespace-nowrap'>
                    Date
                  </th>
                  <th className='text-left text-xs font-semibold uppercase tracking-wider text-slate-400 pb-3 pr-4 whitespace-nowrap'>
                    Action
                  </th>
                  <th className='text-left text-xs font-semibold uppercase tracking-wider text-slate-400 pb-3 pr-4 whitespace-nowrap'>
                    Admin
                  </th>
                  <th className='text-left text-xs font-semibold uppercase tracking-wider text-slate-400 pb-3 pr-4 whitespace-nowrap'>
                    Cible
                  </th>
                  <th className='text-left text-xs font-semibold uppercase tracking-wider text-slate-400 pb-3 whitespace-nowrap'>
                    Détail
                  </th>
                </tr>
              </thead>
              <tbody className='divide-y divide-slate-50'>
                {data.data.map(log => (
                  <tr key={log.id} className='hover:bg-slate-50 transition-colors'>
                    {/* Date */}
                    <td className='py-3 pr-4 whitespace-nowrap text-slate-500 text-xs'>
                      {new Date(log.createdAt).toLocaleString('fr-FR', {
                        dateStyle: 'short',
                        timeStyle: 'short',
                      })}
                    </td>

                    {/* Action badge */}
                    <td className='py-3 pr-4'>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${ACTION_BADGE[log.action]}`}
                      >
                        {ACTION_LABELS[log.action]}
                      </span>
                    </td>

                    {/* Admin */}
                    <td className='py-3 pr-4'>
                      {log.admin ? (
                        <div>
                          <p className='text-slate-800 font-medium text-xs'>
                            {log.admin.firstName} {log.admin.lastName}
                          </p>
                          <p className='text-slate-400 text-xs'>{log.admin.email}</p>
                        </div>
                      ) : (
                        <span className='text-slate-400'>—</span>
                      )}
                    </td>

                    {/* Cible */}
                    <td className='py-3 pr-4 text-slate-600 text-xs whitespace-nowrap'>
                      {log.targetType && log.targetId != null ? `${log.targetType} #${log.targetId}` : '—'}
                    </td>

                    {/* Détail collapsible */}
                    <td className='py-3'>
                      {log.metadata != null ? (
                        <details className='text-xs'>
                          <summary className='cursor-pointer text-slate-500 hover:text-slate-700 select-none'>
                            Voir détail
                          </summary>
                          <pre className='mt-2 p-2 bg-slate-50 border border-slate-200 rounded text-slate-600 text-xs overflow-auto max-w-xs'>
                            {JSON.stringify(log.metadata, null, 2)}
                          </pre>
                        </details>
                      ) : (
                        <span className='text-slate-300 text-xs'>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Pagination ─────────────────────────────────────────── */}
      {data && data.total > 0 && (
        <div className='flex items-center justify-between'>
          <p className='text-xs text-slate-400'>
            {data.total} action{data.total > 1 ? 's' : ''} au total
          </p>
          <div className='flex items-center gap-2'>
            <button
              type='button'
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className='flex items-center gap-1 px-3 py-1.5 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition'
            >
              <ChevronLeft className='w-4 h-4' />
              Précédent
            </button>

            <span className='text-xs text-slate-500 px-2'>
              Page {page} / {totalPages}
            </span>

            <button
              type='button'
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className='flex items-center gap-1 px-3 py-1.5 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition'
            >
              Suivant
              <ChevronRight className='w-4 h-4' />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
